import axios, { AxiosError, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import * as Sentry from '@sentry/react';

export type ApiError = AxiosError<{ message?: string }>;

type ApiConfig = {
  csrfToken: string;
  versionHash?: string;
};

class Api {
  private axios: typeof axios;
  private isConfigured: boolean;

  constructor() {
    this.axios = axios;
    this.isConfigured = false;
  }

  configure = ({ csrfToken, versionHash }: ApiConfig) => {
    if (this.isConfigured) return;

    this.axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';
    this.axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

    if (versionHash) {
      this.axios.defaults.headers.common['X-App-Version'] = versionHash;
    }

    this.axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // If the API returns unauthenticated then our API token expired.
        // By refreshing we hit the backend and let it handle the authentication error
        if (error.response?.status === 401) {
          window.location.reload();
          return error;
        }
        return Promise.reject(error);
      }
    );

    this.isConfigured = true;
  };

  registerRequestInterceptor = (
    onFulfilled: (
      config: InternalAxiosRequestConfig
    ) => InternalAxiosRequestConfig | Promise<InternalAxiosRequestConfig>
  ) => {
    this.axios.interceptors.request.use(onFulfilled);
  };

  delete = async (endpoint: string, config: AxiosRequestConfig | null = null): Promise<AxiosResponse> => {
    try {
      return await this.axios.delete(`/api/${endpoint}`, config as AxiosRequestConfig);
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  };

  get = async (endpoint: string, config: AxiosRequestConfig | null = null): Promise<AxiosResponse> => {
    try {
      return await this.axios.get(`/api/${endpoint}`, config as AxiosRequestConfig);
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  };

  patch = async (
    endpoint: string,
    data: unknown = null,
    config: AxiosRequestConfig | null = null
  ): Promise<AxiosResponse> => {
    try {
      return await this.axios.patch(`/api/${endpoint}`, data, config as AxiosRequestConfig);
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  };

  post = async (
    endpoint: string,
    data: unknown = null,
    config: AxiosRequestConfig | null = null
  ): Promise<AxiosResponse> => {
    try {
      return await this.axios.post(`/api/${endpoint}`, data, config as AxiosRequestConfig);
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  };

  put = async (
    endpoint: string,
    data: unknown = null,
    config: AxiosRequestConfig | null = null
  ): Promise<AxiosResponse> => {
    try {
      return await this.axios.put(`/api/${endpoint}`, data, config as AxiosRequestConfig);
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  };
}

export default new Api();
